import { DisplayType, ResultType, ShowVoteType, VoteType } from '@/components/games/poll/Enums';
import {
  GamePollAdvancedData,
  GamePollAnswerData,
  GamePollData,
  GamePollGeneralData,
  GamePollLayoutData,
  GamePollQuestionData
} from '@/components/games/poll/Data';
import { GameIndicator, GameModel, GameState } from '@/models/GameModel';
import { hexToRGBA, shuffle } from '@/utilities/Utilities';
import useDevice from '@/hooks/useDevice';
import store from '@/store';
import { TransitionTypes } from '@/typings/types/types';
import { AnswerLayoutSettingsModel } from '@/models/settings/AnswerLayoutSettingsModel';

export interface GamePollAnswerState {
  id: string | number; // its number in db, but the first 3 demo content are with strings (legacy)
  headline?: string;
  headline2?: string;
  thumbnail?: string;
  description?: string;
  value: string;
  text?: string;
  placement?: GamePollPlacementState;
  votes: number;
  buttonLabel: string;
  votesPercent?: string; // need to be string
  paused: boolean;
}

export interface GamePollQuestionState {
  question?: string;
  questionImage?: string;
  questionVideo?: string;
  displayType: DisplayType;
  videoWidth?: string;
  videoButtonEnable?: boolean;
  videoPopoverBg?: string;
  videoPopoverCloseColor?: string;
  multiAnswer: boolean;
  multiAnswerOptions?: number;
  pausedLabel?: string;
  answerLayout: AnswerLayoutSettingsModel;
}

export interface GamePollGeneralState {
  answerBtn: string;
  popupText: string;
  confirmText: string;
  cancelText: string;
  sortByResult: boolean;
  randomizeAnswers?: boolean;
  resultIn?: ResultType;
  animateResult?: boolean;
  showResultAfterSubmit?: boolean;
  showConfirmPopup?: boolean;
  selectAnswerBtn?: string;
  popupBackground?: string;
  confirmBackground?: string;
  closeBackground?: string;
  overlayBackground?: string;
  voteType?: VoteType;
  voteButton?: string;
}

export interface GamePollState extends GameState {
  general: GamePollGeneralState;
  question: GamePollQuestionState;
  layout: GamePollLayoutState;
  answers: GamePollAnswerState[];
  elementStyling: string;
  iconStyles: IconStylesState;
  totalVotes: number;
  showVotes: ShowVoteType;
  showAnswerButton: boolean;
  advanced: GamePollAdvancedState;
}

export interface GamePollAdvancedState {
  sound: GamePollAdvancedSoundState;
  animation: GamePollAdvancedAnimationState;
}

export interface GamePollAdvancedSoundState {
  enabled: boolean;
  src?: string;
}

export interface GamePollAdvancedAnimationState {
  enabled: boolean;
  type?: TransitionTypes;
}

export interface IconStylesState {
  checkmarkStyle?: iconStyle;
  checkmarkActiveStyle?: iconStyle;
  radioStyle?: iconStyle;
  radioActiveStyle?: iconStyle;
}

interface iconStyle {
  color?: string;
  backgroundColor?: string;
}

export interface GamePollTranslations {
  confirm?: string;
  yes?: string;
  no?: string;
}

export interface GamePollLayoutState {
  image: string;
  place: GamePollPlacementState;
}

interface GamePollPlacementState {
  id: number;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export class GamePollModel extends GameModel<GamePollData, GamePollState> {
  parseGame(data: GamePollData) {
    const { isMobile } = useDevice();
    const state = this.state;

    state.question = GamePollModel.constructQuestionState(data.question, data.general);
    state.general = GamePollModel.constructGeneralState(data.general);

    state.answers = GamePollModel.parseAnswersState(data);
    if (data.general.result_in === ResultType.PERCENTAGE) {
      GamePollModel.applyRoundPercentages(state.answers);
    }

    if (data.general.randomize_answers === '1') {
      shuffle(state.answers);
    }
    if (data.general.sort_by_result == '1') {
      state.answers.sort(function (a, b) {
        if (a.votes > b.votes) {
          // A greather than b,
          // allow it to be above b.
          return -1;
        } else if (a.votes < b.votes) {
          // A is less than b,
          // allow it to be below b.
          return 1;
        }

        return 0;
      });
    }

    if (state.question.displayType === DisplayType.TEXT) {
      state.elementStyling = GamePollModel.constructElementStyling(data.general);
    }

    if (data.layout) {
      state.layout = GamePollModel.constructLayoutState(data.layout, isMobile);
    }

    if (state.answers) {
      state.totalVotes = 0;
      state.answers.forEach((answer) => {
        state.totalVotes += answer.votes;
      });
    }

    if (data.general.show_votes?.enabled === '1') {
      state.showVotes = data.general.show_votes.type;
    }

    const layoutType =
      (data.question?.display_type === DisplayType.IMAGE ||
        data.question?.display_type === DisplayType.IMAGE_HEADLINE) &&
      state.layout?.image;

    if (data.question.display_type !== DisplayType.VIDEO || data.question.multi_answer === '1' || layoutType) {
      state.showAnswerButton = data.general.show_answer_button ? data.general.show_answer_button === '1' : true;
    }

    state.advanced = state.advanced ?? {};
    state.advanced.sound = GamePollModel.constructAdvancedSoundState(data?.advanced);
    state.advanced.animation = GamePollModel.constructAdvancedAnimationState(data?.advanced);
  }

  private static constructAdvancedSoundState(data: GamePollAdvancedData | undefined): GamePollAdvancedSoundState {
    return {
      enabled: !!data?.sound,
      ...(data?.sound && { src: data.sound })
    };
  }

  private static constructAdvancedAnimationState(
    data: GamePollAdvancedData | undefined
  ): GamePollAdvancedAnimationState {
    return {
      enabled: !!data?.animation,
      ...(data?.animation && { type: data.animation })
    };
  }

  private static parseAnswersState(data: GamePollData): GamePollAnswerState[] {
    return data.question.answers.map((answer) => {
      return GamePollModel.constructAnswerState(answer, data.general.vote_btn);
    });
  }

  private static constructGeneralState(data: GamePollGeneralData): GamePollGeneralState {
    const campaignState = store.getters['campaign/state'];
    const translation = campaignState.translation?.games.poll;

    const answers: GamePollAnswerState[] = [];
    if (data?.randomize_answers === '1') {
      shuffle(answers);
    }

    return {
      sortByResult: data.sort_by_result === '1' || false,
      resultIn: data.result_in || ResultType.PERCENTAGE,
      animateResult: data.animate_result === '1' || false,
      showResultAfterSubmit: data.show_result_after_submit ? data.show_result_after_submit === '1' : false,
      popupBackground: data.popup_background || '#ffffff',
      confirmBackground: data.confirm_background || '#fc7169',
      closeBackground: data.close_background || '#b6bece',
      overlayBackground: hexToRGBA(data?.overlay_background ? data.overlay_background : '#5e6e8d', 0.9),
      ...(data.vote_btn && { voteButton: data.vote_btn }),
      answerBtn: data.answer_btn || 'Answer',
      selectAnswerBtn: data.select_answer_btn || 'Choose an answer',
      voteType: data.vote_type || VoteType.ITEM,
      showConfirmPopup: data.show_confirm_popup === '1' || false,
      confirmText: data.confirm_text || translation?.yes || 'Yes',
      cancelText: data.cancel_text || translation?.no || 'No close again',
      popupText: data.popup_text || translation?.confirm || 'Confirm your vote'
    };
  }

  private static applyRoundPercentages(answers: GamePollAnswerState[]) {
    const total = answers
      .map((answer) => {
        return answer.votes;
      })
      .reduce(function (a, b) {
        return a + b;
      }, 0);
    const pPercent = total / 100;
    const length = answers.length;

    let i = 0;
    let sumCurrent = 0;

    answers = answers.map((val) => {
      if (total === 0) {
        val.votesPercent = '0.0';
      } else {
        i++;

        let percent = parseFloat(
          parseFloat(val.votes / pPercent + '')
            .toFixed(3)
            .slice(0, -2)
        );

        //
        if (i === length && 100 > sumCurrent + percent) {
          percent += 100 - (sumCurrent + percent);
        }

        sumCurrent += percent;
        val.votesPercent = parseFloat(percent + '').toFixed(1);
      }

      return val;
    });

    return answers;
  }

  private static constructAnswerState(data: GamePollAnswerData, defaultButton?: string): GamePollAnswerState {
    const { isMobile } = useDevice();
    const placement = isMobile ? data.placement?.mobile : data.placement?.desktop;

    return {
      id: data.id,
      buttonLabel: data.button_label || defaultButton || 'Answer',
      value: data.value || '',
      votes: data.votes || 0,
      ...(data.description && { description: data.description }),
      ...(data.headline && { headline: data.headline }),
      ...(data.headline2 && { headline2: data.headline2 }),
      ...(data.thumbnail && { thumbnail: data.thumbnail }),
      ...(data.text && { text: data.text }),
      ...(placement && {
        placement: {
          id: Number(placement.id),
          label: placement.label,
          x: Number(placement.x),
          y: Number(placement.y),
          w: Number(placement.w),
          h: Number(placement.h)
        }
      }),
      paused: data.paused === '1'
    };
  }

  private static constructQuestionState(
    data: GamePollQuestionData,
    generalData: GamePollGeneralData
  ): GamePollQuestionState {
    let answerLayout: AnswerLayoutSettingsModel | undefined;

    if (answerLayout) {
      answerLayout.setData(generalData);
    } else {
      answerLayout = new AnswerLayoutSettingsModel(generalData);
    }

    return {
      ...(data.question && { question: data.question }),
      ...(data.question_video?.enable === '1' && { questionVideo: data.question_video.url }),
      ...(data.question_image && { questionImage: data.question_image }),
      ...{ displayType: data.display_type || DisplayType.TEXT },
      ...{ multiAnswer: data.multi_answer === '1' || false },
      ...(data.multi_answer_options && { multiAnswerOptions: Number(data.multi_answer_options) }),

      // type video...
      ...(data.display_type === DisplayType.VIDEO && {
        videoWidth: data.video_width || '640px',
        videoPopoverBg: data.video_popover_bg || '#000000',
        videoPopoverCloseColor: data.video_popover_close_color || '#ffffff',
        videoButtonEnable: data.video_button_enable === '1' || false
      }),
      ...(data.disable_answer && { pausedLabel: data.paused_label || 'This answer is disabled at the moment' }),
      answerLayout
    };
  }

  private static constructLayoutState(data: GamePollLayoutData, isMobile: boolean): GamePollLayoutState {
    const _data = isMobile ? data.mobile : data.desktop;

    return {
      image: _data.image,
      place: {
        id: Number(_data.place.id),
        label: _data.place.label,
        x: Number(_data.place.x),
        y: Number(_data.place.y),
        w: Number(_data.place.w),
        h: Number(_data.place.h)
      }
    };
  }

  /**
   * Constructing element styling, this is very pretty.
   * we have to check if data exists and then add it to the string that we later create as css component
   * @param data
   * @private
   */
  private static constructElementStyling(data: GamePollGeneralData): string {
    return `.poll .question .question__answers:not(.question__answers--images) .question__answer-item {
              ${data.answer_color ? 'color:' + data.answer_color + ';' : ''}
              ${
                data.border_thickness && data.border_color && data.border_color !== ''
                  ? 'border:' + data.border_thickness + 'px solid' + data.border_color + ';'
                  : ''
              }
              ${data.bullets_bg_color ? 'background-color:' + data.bullets_bg_color + ';' : ''}
              ${data.answer_margin ? 'margin-bottom:' + data.answer_margin + 'px;' : ''}
              ${data.answer_padding ? 'padding-bottom:' + Math.ceil(Number(data.answer_padding) / 2) / 10 + 'rem;' : ''}
              ${data.answer_padding ? 'padding-top:' + Math.ceil(Number(data.answer_padding) / 2) / 10 + 'rem;' : ''}
              ${data.width ? 'width:' + data.width : ''}
              }
              .poll .question .question__answers:not(.question__answers--images) .question__answer-item.question__answer-item--selected,
              .category-desktop .poll .question .question__answers .question__answer-item:hover {
                ${data.active_state_color ? 'color:' + data.active_state_color + '!important;' : ''}
                ${data.active_state_bg_color ? 'background-color:' + data.active_state_bg_color + '!important;' : ''}
              }`;
  }

  public getIndicators(): GameIndicator[] {
    return [];
  }

  public isGameValid(): boolean {
    // is valid logic here
    // const data = this.getData();
    return true;
  }

  authorSignature(): string {
    return 'Jannik Fischer';
  }
}
